import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";

export const ChangePasswordCard = () => {
  const qc = useQueryClient();
  const { data: ps } = useQuery({ queryKey: ["password-status"], queryFn: async () => (await api.get("/auth/password-status")).data });
  const hasPassword = ps ? !!ps.has_password : true;
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(""); const [pw, setPw] = useState(""); const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success(hasPassword ? "Password changed" : "Password set — you can now sign in with email + password too");
      setCur(""); setPw(""); setConfirm(""); setOpen(false); qc.invalidateQueries({ queryKey: ["password-status"] });
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not change password"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5 mt-6" data-testid="change-password-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-lg font-bold text-white flex items-center gap-2"><KeyRound size={16} className="text-[#22c3d6]" />{hasPassword ? "Password" : "Set a password"}</h2>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid="change-password-toggle" className="h-8 rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-800 text-xs">{open ? "Cancel" : hasPassword ? "Change password" : "Add password"}</Button>
      </div>
      {!open && <p className="text-sm text-zinc-500 mt-1.5">{hasPassword ? "Change your sign-in password any time — no email link needed." : "You signed up with Google. Add a password to also sign in with your email."}</p>}
      {open && (
        <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3 mt-4">
          {hasPassword && <div><Label className="text-zinc-300 text-xs">Current password</Label><PasswordInput testid="current-password" required value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1 h-9 bg-[#1e222d] border-zinc-700 text-white" /></div>}
          <div><Label className="text-zinc-300 text-xs">New password</Label><PasswordInput testid="new-password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className="mt-1 h-9 bg-[#1e222d] border-zinc-700 text-white" /></div>
          <div><Label className="text-zinc-300 text-xs">Confirm new password</Label><PasswordInput testid="confirm-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 h-9 bg-[#1e222d] border-zinc-700 text-white" /></div>
          <div className="sm:col-span-3 flex justify-end"><Button type="submit" size="sm" disabled={busy} data-testid="change-password-submit" className="h-9 rounded-full bg-[#ff6a13] hover:bg-[#ff8a3d] text-white font-semibold">{busy ? "Saving…" : "Save password"}</Button></div>
        </form>
      )}
    </div>
  );
};
